import * as React from 'react';

import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {
  Toast,
  toastApi,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {useQueryClient} from '@tanstack/react-query';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {DashboardListItem} from 'src/types/byod';
import {useSetDefaultDashboard} from 'src/components/analytics-genesis/byod/api/mutations/useSetDefaultDashboard';
import {useSetGroupDefaultDashboard} from 'src/components/analytics-genesis/byod/api/mutations/useSetGroupDefaultDashboard';
import {reportByodApiError} from 'src/components/analytics-genesis/byod/api/error-handling';
import {SetDefaultModal} from 'src/components/dashboard/set-default-modal';

import css from './home-dashboard-selector.css';


const SENSE_DEFAULT_KEY = '__sense_default__';

type HomeDashboardSelectorProps = {
  selectedDashboardId: string | null;
  onDashboardChange: (id: string | null) => void;
  dashboards: DashboardListItem[];
  homeDashboardId: string | null;
};

export function HomeDashboardSelector({
  selectedDashboardId,
  onDashboardChange,
  dashboards,
  homeDashboardId,
}: HomeDashboardSelectorProps) {
  const {t} = useLabelTranslation();
  const [showModal, setShowModal] = React.useState(false);
  const queryClient = useQueryClient();

  const {mutateAsync: setPersonalDefault} = useSetDefaultDashboard();
  const {mutateAsync: setGroupDefault} = useSetGroupDefaultDashboard();

  const invalidateList = React.useCallback(() => {
    void queryClient.invalidateQueries({queryKey: ['byod-dashboard-list']});
  }, [queryClient]);

  const dropdownOptions: MenuOption[] = React.useMemo(() => {
    const homeKey = homeDashboardId ?? SENSE_DEFAULT_KEY;

    const starProps = {
      iconRight: 'star',
      iconRightType: 'solid' as const,
    };

    const senseDefault: MenuOption = {
      key: SENSE_DEFAULT_KEY,
      label: t('Sense_Default', 'Sense Default'),
      secondaryLabel: t(
        'This_is_the_default_home_dashboard_from_Sense',
        'This is the default home dashboard from Sense',
      ),
      ...(homeKey === SENSE_DEFAULT_KEY ? starProps : {}),
    };

    const byodOptions: MenuOption[] = dashboards.map((d) => ({
      key: d.id,
      label: d.name,
      secondaryLabel: d.description || t('No_description', 'No description'),
      ...(d.id === homeKey ? starProps : {}),
    }));

    const selectedKey = selectedDashboardId ?? SENSE_DEFAULT_KEY;
    const allOptions = [senseDefault, ...byodOptions];

    const selected = allOptions.find((o) => o.key === selectedKey);
    const rest = allOptions.filter((o) => o.key !== selectedKey);

    return [
      ...(selected
        ? [{...selected, classNames: {wrapper: css.selectedOption}}]
        : []),
      ...rest,
    ];
  }, [dashboards, t, selectedDashboardId, homeDashboardId]);

  const selectedKeys = [selectedDashboardId ?? SENSE_DEFAULT_KEY];

  const selectedLabel = React.useMemo(() => {
    if (selectedDashboardId == null) {
      return t('Sense_Default', 'Sense Default');
    }
    return (
      dashboards.find((d) => d.id === selectedDashboardId)?.name ??
      t('Sense_Default', 'Sense Default')
    );
  }, [selectedDashboardId, dashboards, t]);

  const resolveLabel = React.useCallback(
    (option: MenuOption) => {
      return option.label ?? t('NA', 'N/A');
    },
    [t],
  );

  const handleOptionSelect = React.useCallback(
    (option: MenuOption) => {
      onDashboardChange(option.key === SENSE_DEFAULT_KEY ? null : option.key);
    },
    [onDashboardChange],
  );

  const isFilledStar =
    selectedDashboardId != null && selectedDashboardId === homeDashboardId;
  const isStarFilled =
    (selectedDashboardId == null && homeDashboardId == null) || isFilledStar;

  const handleStarClick = async () => {
    if (selectedDashboardId == null) {
      return;
    }

    if (isFilledStar) {
      try {
        await setPersonalDefault({
          dashboardId: selectedDashboardId,
          remove: true,
        });
        invalidateList();
        onDashboardChange(null);
      } catch (error) {
        reportByodApiError(error, {
          action: 'byod-unset-home-dashboard',
          dashboardId: selectedDashboardId,
        });
        toastApi.show(
          <Toast initialFocus={0} semantic="danger">
            <ToastTitle>
              {t(
                'Could_not_unset_the_default_dashboard_Please_try_again',
                'Could not unset the default dashboard. Please try again.',
              )}
            </ToastTitle>
          </Toast>,
        );
      }
    } else {
      setShowModal(true);
    }
  };

  const handleConfirmSetHome = async (scope: 'self' | 'everyone') => {
    setShowModal(false);
    if (!selectedDashboardId) {
      return;
    }

    try {
      if (scope === 'self') {
        await setPersonalDefault({dashboardId: selectedDashboardId});
      } else {
        await setGroupDefault({dashboardId: selectedDashboardId});
      }
      invalidateList();
      toastApi.show(
        <Toast initialFocus={0} semantic="success">
          <ToastTitle>
            {t('Dashboard_set_as_default', 'Dashboard set as default')}
          </ToastTitle>
        </Toast>,
      );
    } catch (error) {
      reportByodApiError(error, {
        action: 'byod-set-home-dashboard',
        dashboardId: selectedDashboardId,
      });
      toastApi.show(
        <Toast initialFocus={0} semantic="danger">
          <ToastTitle>
            {t(
              'Could_not_set_this_dashboard_as_default_Please_try_again',
              'Could not set this dashboard as default. Please try again.',
            )}
          </ToastTitle>
        </Toast>,
      );
    }
  };

  return (
    <div className={css.wrapper}>
      <Tooltip
        title={
          isStarFilled
            ? t('Default_dashboard', 'Default dashboard')
            : t('Set_as_default_dashboard', 'Set as default dashboard')
        }
        placement="bottom"
      >
        <button
          type="button"
          className={css.starButton}
          onClick={handleStarClick}
          aria-label={
            isStarFilled
              ? t('Default_dashboard', 'Default dashboard')
              : t('Set_as_default_dashboard', 'Set as default dashboard')
          }
        >
          <Icon
            name="star"
            type={isStarFilled ? 'solid' : 'regular'}
            size="medium"
            color={isStarFilled ? 'favorite' : 'secondary'}
          />
        </button>
      </Tooltip>

      <SimpleButtonDropdown
        type="tertiary"
        size="medium"
        isFluid={false}
        onOptionSelect={handleOptionSelect}
        selectedKeys={selectedKeys}
        options={dropdownOptions}
        allowSearch
        resolveLabel={resolveLabel}
        anchorPosition="bottom-start"
        menuClassNames={{wrapper: css.menuPanel}}
      >
        {selectedLabel}
      </SimpleButtonDropdown>

      {showModal && (
        <SetDefaultModal
          onClose={() => setShowModal(false)}
          onConfirm={handleConfirmSetHome}
        />
      )}
    </div>
  );
}
